import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createTransactionAction } from '@symphony-solo/domain/transaction/src';
import { TransactionFlowType } from '@symphony-solo/domain/transaction/src/lib/enums/transaction-flow-type.enum';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ApiResponseCode } from '@symphony-solo/shared/http/src';
import { ADD_CARD, LOCATION_SELECTION_TABS, SELECT_APP_SIMPLE_TABS } from '@symphony-solo/shared/utils/src';
import { some } from 'lodash';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CardStatus, PaymentCardsFlowType } from '../enums';
import { CanPayResults } from '../enums/can-pay-response.enum';
import { PaymentCardsService } from '../services';
import {
  checkCanPayErrorAction,
  checkCanPaySuccessAction,
  deletePaymentCardAction,
  deletePaymentCardErrorAction,
  deletePaymentCardSuccessAction,
  loadPaymentCardsAction,
  loadPaymentCardsErrorAction,
  loadPaymentCardsSuccessAction,
  selectPaymentCardAction,
} from './payment-cards.actions';
import { AuthenticationFacade } from '@symphony-solo/domain/authentication/src';

@Injectable()
export class PaymentCardsEffects {
  constructor(
    private action$: Actions,
    private paymentCardsService: PaymentCardsService,
    private navController: NavController,
    private authenticationFacade: AuthenticationFacade
  ) {}

  protected loadPaymentCards$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadPaymentCardsAction),
      withLatestFrom(this.authenticationFacade.isAuthenticated$),
      switchMap(([{ flow }, isAuthenticated]) => {
        if (isAuthenticated) {
          return this.paymentCardsService.getPaymentCards().pipe(
            map((paymentCards) => {
              return loadPaymentCardsSuccessAction({ paymentCards, flow });
            }),
            catchError(() => {
              const failure = new AppStateFailureBuilder()
                .setFailureType(FailureType.API)
                .addFailure(new AppStateFailureItemWithErrorCode('Error on loading Payment Cards', ApiResponseCode.Server_Error))
                .build();
              return of(loadPaymentCardsErrorAction({ failure }));
            })
          );
        }
        return of(loadPaymentCardsSuccessAction({ paymentCards: null, flow }));
      })
    )
  );

  protected loadPaymentCardsError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(loadPaymentCardsErrorAction),
        tap(() => {
          this.navController.navigateRoot(SELECT_APP_SIMPLE_TABS);
        })
      ),
    {
      dispatch: false,
    }
  );

  protected loadPaymentCardsSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType(loadPaymentCardsSuccessAction),
      map(({ paymentCards, flow }) => {
        if (paymentCards?.length === 0) {
          return checkCanPayErrorAction({ canPayStatus: CanPayResults.NoPaymentCard, flow });
        }
        if (some(paymentCards, (card) => card.cardState === CardStatus.Valid || card.cardState === CardStatus.ExpiringSoon)) {
          return checkCanPaySuccessAction({ flow });
        }
        return checkCanPayErrorAction({ canPayStatus: CanPayResults.PaymentCardsExpired, flow });
      })
    )
  );

  protected checkCanPaySuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(checkCanPaySuccessAction),
        tap(({ flow }) => {
          if (flow === PaymentCardsFlowType.LocationSelection) {
            this.navController.navigateRoot(LOCATION_SELECTION_TABS);
          }
        })
      ),
    {
      dispatch: false,
    }
  );

  protected checkCanPayError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(checkCanPayErrorAction),
        tap(({ flow }) => {
          if (flow === PaymentCardsFlowType.LocationSelection) {
            this.navController.navigateForward(ADD_CARD);
          }
        })
      ),
    {
      dispatch: false,
    }
  );

  protected selectPaymentCard$ = createEffect(() =>
    this.action$.pipe(
      ofType(selectPaymentCardAction),
      switchMap((payload) => {
        return of(createTransactionAction({ paymentCardId: payload.paymentCard.paymentCardID, transactionFlow: TransactionFlowType.Pay }));
      })
    )
  );

  protected deletePaymentCard$ = createEffect(() =>
    this.action$.pipe(
      ofType(deletePaymentCardAction),
      switchMap((paymentCardID) => {
        return this.paymentCardsService.deletePaymentCard(paymentCardID.paymentCardID).pipe(
          map(() => {
            return deletePaymentCardSuccessAction();
          }),
          catchError(() => {
            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.API)
              .addFailure(new AppStateFailureItemWithErrorCode('Error on deleting payment card', ApiResponseCode.Server_Error))
              .build();
            return of(deletePaymentCardErrorAction({ failure }));
          })
        );
      })
    )
  );
}
